
import React, { useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Phone,
  ShieldCheck,
  Loader2,
  ArrowRight,
  RefreshCw,
  Edit3,
  LockKeyhole,
  UserPlus,
  CheckCircle2,
  Zap,
  MapPinned,
  BadgeCheck,
} from "lucide-react";

import logo from "../../assets/Packky.png";
import front from "../../assets/rightbanner.webp";
import delhi from "../../assets/indiagate.webp";
import noida from "../../assets/noida.webp";
import ghaziabad from "../../assets/ghaziabad.webp";
import gurugram from "../../assets/gurugram.webp";
import useGatewayLogin from "../Services/Login/useGatewayLogin";
import { GlobalContext } from "../../Context/GlobalContext";

const Gateway = ({ isBookingFlow = false, onNext, selectedCity }) => {
  const [loginStep, setLoginStep] = useState(1);
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [screen, setScreen] = useState("LOGIN");
  const [isPrivacyAccepted, setIsPrivacyAccepted] = useState(false);
  const [showPrivacyBox, setShowPrivacyBox] = useState(false);
  const [verifiedUserData, setVerifiedUserData] = useState(null);

  const { saveUser } = useContext(GlobalContext);
  const { gatewayLoading, gatewayLoginApi } = useGatewayLogin();

  const [registerData, setRegisterData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const otpRefs = useRef([]);
  const navigate = useNavigate();

  const isOtpStep = loginStep === 2;

  const normalizeCityName = (city = "") => {
    const value = String(city).trim().toLowerCase();

    if (value.includes("delhi")) return "Delhi";
    if (value.includes("noida")) return "Noida";
    if (value.includes("gurugram") || value.includes("gurgaon"))
      return "Gurugram";
    if (value.includes("gaziabad") || value.includes("ghaziabad"))
      return "Ghaziabad";

    return "Delhi";
  };

  const finalSelectedCity = normalizeCityName(
    selectedCity || sessionStorage.getItem("selectedCity") || "Delhi",
  );

  const cityImages = {
    Delhi: delhi,
    Noida: noida,
    Ghaziabad: ghaziabad,
    Gaziabad: ghaziabad,
    Gurugram: gurugram,
    Gurgaon: gurugram,
  };

  const rightImage = cityImages[finalSelectedCity] || front;

  const completeLogin = (userData, message = "Login successful") => {
    toast.success(message);

    if (userData) {
      saveUser(userData);
    }

    sessionStorage.setItem("login_mobile", mobileNumber);

    setTimeout(() => {
      if (isBookingFlow && onNext) {
        onNext(userData?.mobile || userData?.phone || mobileNumber, userData);
      } else {
        navigate("/bookings");
      }
    }, 1000);
  };

  const handleSendOtp = async () => {
    if (mobileNumber.length !== 10) {
      toast.error("Please enter valid 10 digit mobile number");
      return;
    }

    const result = await gatewayLoginApi({
      mobileNumber,
      otp: "",
      step: "1",
    });

    if (result?.responseCode === "00") {
      toast.success(result?.responseMessage || "OTP sent successfully");
      setLoginStep(2);

      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
    } else {
      toast.error(result?.responseMessage || "Unable to send OTP");
    }
  };

  const handleVerifyOtp = async () => {
    const finalOtp = otp.join("");

    if (finalOtp.length !== 6) {
      toast.error("Please enter complete OTP");
      return;
    }

    const result = await gatewayLoginApi({
      mobileNumber,
      otp: finalOtp,
      step: "2",
    });

    if (result?.responseCode === "00") {
      const userData = result?.data || result?.response;

      const privacyPolicy = String(userData?.privacyPolicy || "").toUpperCase();
      const isOldUser = privacyPolicy === "Y";

      if (isOldUser) {
        setShowPrivacyBox(false);
        setVerifiedUserData(null);
        completeLogin(userData, result?.responseMessage || "Login successful");
        return;
      }

      setVerifiedUserData(userData);
      setShowPrivacyBox(true);

      if (!isPrivacyAccepted) {
        toast.error("Please accept Privacy Policy and Terms & Conditions");
        return;
      }

      completeLogin(userData, result?.responseMessage || "Login successful");
    } else {
      toast.error(result?.responseMessage || "Invalid OTP");
    }
  };

  const handleAcceptAndContinue = () => {
    if (!isPrivacyAccepted) {
      toast.error("Please accept Privacy Policy and Terms & Conditions");
      return;
    }

    if (!verifiedUserData) {
      toast.error("Please verify OTP again");
      return;
    }

    completeLogin(verifiedUserData, "Login successful");
  };

  const handleLogin = () => {
    if (loginStep === 1) {
      handleSendOtp();
      return;
    }

    handleVerifyOtp();
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/[^0-9]/g, "");

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleChangeNumber = () => {
    setLoginStep(1);
    setOtp(["", "", "", "", "", ""]);
    setIsPrivacyAccepted(false);
    setShowPrivacyBox(false);
    setVerifiedUserData(null);
  };

  const handleResendOtp = async () => {
    setOtp(["", "", "", "", "", ""]);

    const result = await gatewayLoginApi({
      mobileNumber,
      otp: "",
      step: "1",
    });

    if (result?.responseCode === "00") {
      toast.success(result?.responseMessage || "OTP resent successfully");

      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
    } else {
      toast.error(result?.responseMessage || "Unable to resend OTP");
    }
  };

  return (
    <>
      <ToastContainer />

      <div className="relative w-full overflow-hidden px-3 py-8 sm:px-4 lg:px-0">
        <div className="pointer-events-none absolute left-1/2 top-8 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-300/20 blur-3xl" />
        <div className="pointer-events-none absolute bottom-8 right-8 h-72 w-72 rounded-full bg-sky-300/20 blur-3xl" />

        <div className="relative z-10 mx-auto mt-12 grid w-full max-w-[1020px] overflow-hidden rounded-[26px] border border-white/80 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.16)] sm:mt-6 md:mt-12 lg:mt-[140px] lg:h-[790px] lg:grid-cols-[57%_43%]">
          {/* LEFT FORM */}
          <div className="flex h-full min-h-0 flex-col bg-white p-5 sm:p-6 lg:px-8 lg:py-6">
            {/* TOP HEADER */}
            <div className="shrink-0">
              <div className="mb-3 flex items-center justify-between gap-4">
                <img src={logo} alt="Packky" className="h-12 w-auto" />

                <div className="hidden rounded-full bg-blue-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-blue-700 sm:inline-flex">
                  Secure Login
                </div>
              </div>

              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-blue-700">
                <ShieldCheck size={13} />
                OTP Verification
              </div>
            </div>

            <div
              className={`flex flex-1 flex-col justify-start ${
                isOtpStep ? "pt-5" : "pt-10"
              }`}
            >
              {screen === "LOGIN" && (
                <>
                  <div className={isOtpStep ? "mb-4" : "mb-6"}>
                    <h2
                      className={`font-bold tracking-tight text-slate-900 ${
                        isOtpStep ? "text-[28px]" : "text-[30px]"
                      }`}
                    >
                      Welcome to Packky
                    </h2>

                    <p
                      className={`mt-2 max-w-[430px] leading-6 text-slate-500 ${
                        isOtpStep ? "text-[14px]" : "text-[15px]"
                      }`}
                    >
                      Login with your mobile number to continue your booking
                      securely.
                    </p>
                  </div>

                  {/* MOBILE NUMBER */}
                  <div className="mb-3">
                    <label className="mb-2 block text-[14px] font-semibold text-slate-600">
                      Mobile Number
                    </label>

                    <div className="group flex h-[50px] items-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 transition focus-within:border-blue-500 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-100">
                      <span className="flex h-full items-center gap-2 border-r border-slate-200 px-4 text-sm font-semibold text-slate-500">
                        <Phone size={16} />
                        +91
                      </span>

                      <input
                        type="tel"
                        placeholder="Enter mobile number"
                        value={mobileNumber}
                        maxLength={10}
                        disabled={loginStep === 2}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          setMobileNumber(value);
                          setIsPrivacyAccepted(false);
                          setShowPrivacyBox(false);
                          setVerifiedUserData(null);
                        }}
                        className="w-full bg-transparent px-4 py-3 text-[16px] font-medium text-slate-700 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed disabled:opacity-70"
                      />
                    </div>
                  </div>

                  {/* OTP ONLY AFTER CONTINUE */}
                  {loginStep === 2 && (
                    <div className="mb-3">
                      <div className="mb-2 flex items-center justify-between">
                        <label className="text-[14px] font-semibold text-slate-700">
                          Enter OTP
                        </label>

                        <button
                          type="button"
                          onClick={handleChangeNumber}
                          disabled={gatewayLoading}
                          className="inline-flex items-center gap-1 text-[12px] font-bold text-blue-600 underline disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Edit3 size={12} />
                          Change Number
                        </button>
                      </div>

                     <div className="flex w-full justify-between gap-1 sm:gap-2">
                        {otp.map((digit, index) => (
                          <input
                            key={index}
                            ref={(el) => (otpRefs.current[index] = el)}
                            type="text"
                            maxLength="1"
                            value={digit}
                            onChange={(e) => handleOtpChange(e, index)}
                            onKeyDown={(e) => handleOtpKeyDown(e, index)}
                            className="h-11 flex-1 max-w-[44px] rounded-2xl border border-slate-200 bg-slate-50 text-center text-lg font-bold text-slate-800 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100 sm:h-12 sm:w-12 sm:max-w-none sm:flex-none sm:text-xl"
                          />
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={gatewayLoading}
                        className="mt-2 inline-flex items-center gap-1 text-[12px] font-bold text-blue-600 underline disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <RefreshCw size={12} />
                        Resend OTP
                      </button>
                    </div>
                  )}

                  {showPrivacyBox && (
                    <div className="mb-3 mt-3 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-2.5">
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isPrivacyAccepted}
                          onChange={(e) =>
                            setIsPrivacyAccepted(e.target.checked)
                          }
                          className="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded border-blue-300 accent-blue-600"
                        />

                        <span className="text-[12.5px] leading-5 text-slate-600">
                          New users must agree to Packky's{" "}
                          <a
                            href="/privacy-policy"
                            className="font-bold text-blue-700 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Privacy Policy
                          </a>{" "}
                          and{" "}
                          <a
                            href="/term-condition"
                            className="font-bold text-blue-700 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Terms & Conditions
                          </a>
                          .
                        </span>
                      </label>
                    </div>
                  )}

                  {/* CONTINUE / VERIFY BUTTON */}
                  <button
                    onClick={
                      showPrivacyBox ? handleAcceptAndContinue : handleLogin
                    }
                    disabled={gatewayLoading}
                    className="group flex h-[49px] w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 text-[15px] font-bold text-white shadow-lg shadow-slate-300 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {gatewayLoading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Please wait...
                      </>
                    ) : loginStep === 1 ? (
                      <>
                        Continue
                        <ArrowRight
                          size={18}
                          className="transition group-hover:translate-x-0.5"
                        />
                      </>
                    ) : (
                      <>
                        {showPrivacyBox ? "Accept & Continue" : "Verify"}
                        <ArrowRight
                          size={18}
                          className="transition group-hover:translate-x-0.5"
                        />
                      </>
                    )}
                  </button>

                  {/* 3 CARDS BELOW BUTTON */}
                  <div
                    className={`grid grid-cols-3 ${
                      isOtpStep ? "mt-4 gap-2.5" : "mt-6 gap-3"
                    }`}
                  >
                    <div
                      className={`rounded-2xl border border-blue-100 bg-blue-50/80 ${
                        isOtpStep ? "p-2.5" : "p-4"
                      }`}
                    >
                      <div
                        className={`flex items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm ${
                          isOtpStep ? "mb-1.5 h-8 w-8" : "mb-2.5 h-10 w-10"
                        }`}
                      >
                        <Zap size={isOtpStep ? 16 : 18} />
                      </div>

                      <h4
                        className={`font-bold text-slate-800 ${
                          isOtpStep ? "text-[13px]" : "text-[14px]"
                        }`}
                      >
                        Fast Booking
                      </h4>

                      <p
                        className={`text-slate-500 ${
                          isOtpStep
                            ? "mt-1 text-[11.5px] leading-4"
                            : "mt-1 text-[12px] leading-5"
                        }`}
                      >
                        Book delivery quickly.
                      </p>
                    </div>

                    <div
                      className={`rounded-2xl border border-blue-100 bg-blue-50/80 ${
                        isOtpStep ? "p-2.5" : "p-4"
                      }`}
                    >
                      <div
                        className={`flex items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm ${
                          isOtpStep ? "mb-1.5 h-8 w-8" : "mb-2.5 h-10 w-10"
                        }`}
                      >
                        <MapPinned size={isOtpStep ? 16 : 18} />
                      </div>

                      <h4
                        className={`font-bold text-slate-800 ${
                          isOtpStep ? "text-[13px]" : "text-[14px]"
                        }`}
                      >
                        Saved Address
                      </h4>

                      <p
                        className={`text-slate-500 ${
                          isOtpStep
                            ? "mt-1 text-[11.5px] leading-4"
                            : "mt-1 text-[12px] leading-5"
                        }`}
                      >
                        Use saved pickup.
                      </p>
                    </div>

                    <div
                      className={`rounded-2xl border border-blue-100 bg-blue-50/80 ${
                        isOtpStep ? "p-2.5" : "p-4"
                      }`}
                    >
                      <div
                        className={`flex items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm ${
                          isOtpStep ? "mb-1.5 h-8 w-8" : "mb-2.5 h-10 w-10"
                        }`}
                      >
                        <BadgeCheck size={isOtpStep ? 16 : 18} />
                      </div>

                      <h4
                        className={`font-bold text-slate-800 ${
                          isOtpStep ? "text-[13px]" : "text-[14px]"
                        }`}
                      >
                        Secure OTP
                      </h4>

                      <p
                        className={`text-slate-500 ${
                          isOtpStep
                            ? "mt-1 text-[11.5px] leading-4"
                            : "mt-1 text-[12px] leading-5"
                        }`}
                      >
                        Verified login.
                      </p>
                    </div>
                  </div>

                  {/* WHY LOGIN */}
                  <div
                    className={`rounded-2xl border border-slate-100 bg-slate-50 px-4 ${
                      isOtpStep ? "mt-3 py-2.5" : "mt-4 py-4"
                    }`}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <CheckCircle2
                        size={isOtpStep ? 15 : 17}
                        className="text-green-600"
                      />

                      <h4
                        className={`font-bold text-slate-800 ${
                          isOtpStep ? "text-[14px]" : "text-[15px]"
                        }`}
                      >
                        Why login with Packky?
                      </h4>
                    </div>

                    <div
                      className={`grid grid-cols-2 gap-x-4 text-slate-600 ${
                        isOtpStep
                          ? "gap-y-1.5 text-[12.5px]"
                          : "gap-y-2.5 text-[13px]"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                        Faster checkout
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                        Live tracking
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                        Saved preferences
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                        Secure account
                      </div>
                    </div>
                  </div>
                </>
              )}

              {screen === "OTPVERIFICATION" && (
                <>
                  <h2 className="mb-1 text-[26px] font-bold tracking-tight text-slate-900">
                    OTP Verification
                  </h2>

                  <p className="mb-5 text-sm leading-6 text-slate-500">
                    Enter the 6-digit code sent to your mobile number.
                  </p>

                  <div className="mt-5 flex gap-2">
                    <button
                      onClick={handleVerifyOtp}
                      disabled={gatewayLoading}
                      className="flex h-[47px] flex-1 items-center justify-center gap-2 rounded-2xl bg-[#1f2d3f] px-4 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {gatewayLoading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Please wait...
                        </>
                      ) : (
                        "Verify & Continue"
                      )}
                    </button>

                    <button
                      onClick={handleResendOtp}
                      disabled={gatewayLoading}
                      className="rounded-2xl border border-slate-200 px-4 text-sm font-bold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Re-send OTP
                    </button>
                  </div>
                </>
              )}

              {screen === "FORGOTPASSWORD" && (
                <>
                  <h2 className="mb-1 text-[26px] font-bold tracking-tight text-slate-900">
                    Forgot Password?
                  </h2>

                  <p className="mb-5 text-sm leading-6 text-slate-500">
                    Enter your registered phone number.
                  </p>

                  <input
                    type="tel"
                    placeholder="Mobile Number"
                    className="mb-4 h-[50px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-[15px] text-slate-700 outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                  />

                  <div className="flex gap-2">
                    <button
                      className="h-[47px] rounded-2xl bg-[#1f2d3f] px-5 text-sm font-bold text-white"
                      onClick={() => setScreen("OTPVERIFICATIONFORGET")}
                    >
                      Continue
                    </button>

                    <button
                      className="h-[47px] rounded-2xl border border-slate-200 px-5 text-sm font-bold text-slate-700"
                      onClick={() => setScreen("LOGIN")}
                    >
                      Back
                    </button>
                  </div>
                </>
              )}

              {screen === "REGISTER" && (
                <>
                  <h2 className="mb-1 text-[26px] font-bold tracking-tight text-slate-900">
                    Create Account
                  </h2>

                  <p className="mb-5 text-sm leading-6 text-slate-500">
                    Register with your details.
                  </p>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <input
                      type="text"
                      placeholder="First Name"
                      value={registerData.firstName}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          firstName: e.target.value,
                        })
                      }
                      className="h-[47px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-[14px] text-slate-700 outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />

                    <input
                      type="text"
                      placeholder="Last Name"
                      value={registerData.lastName}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          lastName: e.target.value,
                        })
                      }
                      className="h-[47px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-[14px] text-slate-700 outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />
                  </div>

                  <input
                    type="email"
                    placeholder="Email Address"
                    value={registerData.email}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        email: e.target.value,
                      })
                    }
                    className="mt-3 h-[47px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-[14px] text-slate-700 outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                  />

                  <input
                    type="text"
                    placeholder="Phone Number"
                    value={registerData.phone}
                    maxLength={10}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");

                      setRegisterData({
                        ...registerData,
                        phone: value,
                      });
                    }}
                    className="mt-3 h-[47px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-[14px] text-slate-700 outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                  />

                  <button
                    className="mt-4 h-[47px] w-full rounded-2xl border border-slate-200 text-sm font-bold text-slate-700"
                    onClick={() => setScreen("LOGIN")}
                  >
                    Back to Login
                  </button>
                </>
              )}

              {screen === "OTPVERIFICATIONFORGET" && (
                <>
                  <h2 className="mb-3 text-[26px] font-bold tracking-tight text-slate-900">
                    Verify OTP
                  </h2>

                  <div className="mb-4 flex justify-between gap-2">
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                      <input
                        key={item}
                        maxLength={1}
                        className="h-11 w-11 rounded-2xl border border-slate-200 bg-slate-50 text-center font-bold outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                      />
                    ))}
                  </div>

                  <button
                    onClick={() => setScreen("RESET_PASSWORD")}
                    className="h-[47px] rounded-2xl bg-[#1f2d3f] px-5 text-sm font-bold text-white"
                  >
                    {showPrivacyBox ? "Accept & Continue" : "Verify"}
                  </button>
                </>
              )}

              {screen === "RESET_PASSWORD" && (
                <>
                  <h2 className="mb-1 text-[26px] font-bold tracking-tight text-slate-900">
                    Reset Password
                  </h2>

                  <p className="mb-5 text-sm leading-6 text-slate-500">
                    Set a new password.
                  </p>

                  <input
                    type="password"
                    placeholder="New Password"
                    className="mb-3 h-[47px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-[14px] text-slate-700 outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                  />

                  <input
                    type="password"
                    placeholder="Confirm Password"
                    className="mb-4 h-[47px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-[14px] text-slate-700 outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                  />

                  <div className="flex gap-2">
                    <button
                      onClick={() => setScreen("LOGIN")}
                      className="h-[47px] rounded-2xl bg-[#1f2d3f] px-5 text-sm font-bold text-white"
                    >
                      Reset
                    </button>

                    <button
                      className="h-[47px] rounded-2xl border border-slate-200 px-5 text-sm font-bold text-slate-700"
                      onClick={() => setScreen("LOGIN")}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* RIGHT DESIGN */}
          <div className="relative hidden h-full w-full items-center justify-center overflow-hidden bg-gradient-to-br from-[#eaf7ff] via-[#b9e5ff] to-[#188bdd] lg:flex">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/30 blur-3xl" />
            <div className="absolute -bottom-24 -left-20 h-80 w-80 rounded-full bg-blue-300/40 blur-3xl" />
           <div className="relative z-10 flex h-full w-full items-center justify-center">
<img
  src={front}
  alt="delivery"
  className="absolute inset-0 h-full w-full object-con"
/>
</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Gateway;

